import { useCallback, useEffect, useRef, useState } from 'react';

const AUTO_COLLAPSE_MS = 5_000;

export interface AutoCollapseState {
  collapsed: boolean;
  pinned: boolean;
  setCollapsed: (value: boolean) => void;
  togglePinned: () => void;
  /** Rilancia il timer di inattività (da chiamare su mousemove/keydown). */
  touch: () => void;
  /** Espande su hover quando è collassata e non pinnata. */
  expand: () => void;
  collapse: () => void;
}

/**
 * Stato condiviso per le sidebar auto-collassabili W3:
 * - auto-collapse dopo 5s di inattività;
 * - pin (blocco): finché è attivo il pannello resta espanso e il timer è disarmato;
 * - persistenza del pin in localStorage per-pannello.
 */
export function useAutoCollapse(storageKey: string, initialCollapsed = false): AutoCollapseState {
  const [pinned, setPinned] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  });
  const [collapsed, setCollapsed] = useState<boolean>(() => (pinned ? false : initialCollapsed));
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const touch = useCallback(() => {
    clearTimer();
    if (pinned) return;
    timerRef.current = setTimeout(() => setCollapsed(true), AUTO_COLLAPSE_MS);
  }, [clearTimer, pinned]);

  useEffect(() => {
    if (!collapsed && !pinned) touch();
    if (pinned) clearTimer();
    return clearTimer;
  }, [collapsed, pinned, touch, clearTimer]);

  const togglePinned = useCallback(() => {
    setPinned((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(storageKey, next ? '1' : '0');
      } catch {
        /* storage non disponibile: il pin resta solo in sessione */
      }
      if (next) setCollapsed(false);
      return next;
    });
  }, [storageKey]);

  const expand = useCallback(() => setCollapsed(false), []);
  const collapse = useCallback(() => {
    if (!pinned) setCollapsed(true);
  }, [pinned]);

  return { collapsed, pinned, setCollapsed, togglePinned, touch, expand, collapse };
}
